import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthContext } from '../contexts'
import { useAuth } from '../hooks/useAuth'
import { Spinner } from './Spinner'
import type { UserRole } from '../types/auth'

/**
 * Generic protected route (any authenticated user)
 * @deprecated Use AuthenticatedRoute or RoleProtectedRoute instead
 */
export function ProtectedRoute({ children }: any): any {
  const { isAuthorized } = useAuthContext()
  const location = useLocation()

  // Show loading spinner while auth state is being determined
  if (isAuthorized === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size={50} color="blue" />
      </div>
    )
  }

  // Redirect to login with return URL if not authorized
  if (!isAuthorized) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return children ? children : <Outlet />
}

/**
 * Loading spinner component for protected routes
 */
function LoadingSpinner({ message = 'Verifying access...' }: { message?: string }) {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <Spinner size={50} color="blue" />
        <p className="mt-4">{message}</p>
      </div>
    </div>
  )
}

/**
 * Unauthorized access component
 */
export function UnauthorizedAccess({ requiredRole }: { requiredRole?: string }) {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center max-w-md p-8 bg-red-50 border border-red-200 rounded-lg">
        <svg className="w-16 h-16 text-red-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <h2 className="text-2xl font-bold text-red-800 mb-2">Unauthorized Access</h2>
        <p className="text-red-700 mb-4">
          {requiredRole
            ? `You need ${requiredRole} role to access this page.`
            : 'You do not have permission to access this page.'}
        </p>
        <a
          href="/"
          className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Return to Home
        </a>
      </div>
    </div>
  )
}

/**
 * Partner-only protected route
 *
 * Only users with PARTNER role can access this route.
 * Unauthenticated users are redirected to /login.
 * Authenticated non-partners see unauthorized message.
 *
 * @example
 * ```tsx
 * <Route path="/partner/*" element={
 *   <PartnerProtectedRoute>
 *     <PartnerDashboard />
 *   </PartnerProtectedRoute>
 * } />
 * ```
 */
export function PartnerProtectedRoute() {
  const { user, isPartner, loading } = useAuth()

  console.log('🛡️ PartnerProtectedRoute:', { hasUser: !!user, isPartner: isPartner(), loading })

  if (loading) {
    return <LoadingSpinner message="Verifying partner access..." />
  }

  if (!user) {
    console.log('❌ Not authenticated, redirecting to login')
    return <Navigate to="/login" replace />
  }

  if (!isPartner()) {
    console.log('❌ Not a partner, showing unauthorized message')
    return <UnauthorizedAccess requiredRole="PARTNER" />
  }

  console.log('✅ Partner access granted!')
  return <Outlet />
}

/**
 * Customer-only protected route
 *
 * Only users with CUSTOMER role can access this route.
 * Unauthenticated users are redirected to /login.
 * Authenticated non-customers see unauthorized message.
 *
 * @example
 * ```tsx
 * <Route path="/profile" element={
 *   <CustomerProtectedRoute>
 *     <CustomerProfile />
 *   </CustomerProtectedRoute>
 * } />
 * ```
 */
export function CustomerProtectedRoute() {
  const { user, isCustomer, loading } = useAuth()

  console.log('🛡️ CustomerProtectedRoute:', { hasUser: !!user, isCustomer: isCustomer(), loading })

  if (loading) {
    return <LoadingSpinner message="Verifying customer access..." />
  }

  if (!user) {
    console.log('❌ Not authenticated, redirecting to login')
    return <Navigate to="/login" replace />
  }

  if (!isCustomer()) {
    console.log('❌ Not a customer, showing unauthorized message')
    return <UnauthorizedAccess requiredRole="CUSTOMER" />
  }

  console.log('✅ Customer access granted!')
  return <Outlet />
}

/**
 * Guest-only protected route
 *
 * Only users with GUEST role can access this route.
 * Unauthenticated users are redirected to /login.
 * Authenticated non-guests see unauthorized message.
 *
 * @example
 * ```tsx
 * <Route path="/guest/devices" element={
 *   <GuestProtectedRoute>
 *     <GuestDeviceList />
 *   </GuestProtectedRoute>
 * } />
 * ```
 */
export function GuestProtectedRoute() {
  const { user, isGuest, loading } = useAuth()

  console.log('🛡️ GuestProtectedRoute:', { hasUser: !!user, isGuest: isGuest(), loading })

  if (loading) {
    return <LoadingSpinner message="Verifying guest access..." />
  }

  if (!user) {
    console.log('❌ Not authenticated, redirecting to login')
    return <Navigate to="/login" replace />
  }

  if (!isGuest()) {
    console.log('❌ Not a guest, showing unauthorized message')
    return <UnauthorizedAccess requiredRole="GUEST" />
  }

  console.log('✅ Guest access granted!')
  return <Outlet />
}

/**
 * Flexible multi-role protected route
 *
 * Accepts an array of allowed roles. User must have ONE of the roles.
 * Unauthenticated users are redirected to /login.
 * Authenticated users without any required role see unauthorized message.
 *
 * @example
 * ```tsx
 * // Allow both ADMIN and PARTNER
 * <Route path="/management/*" element={
 *   <RoleProtectedRoute allowedRoles={['ADMIN', 'PARTNER']}>
 *     <ManagementDashboard />
 *   </RoleProtectedRoute>
 * } />
 *
 * // Allow all authenticated users
 * <Route path="/dashboard" element={
 *   <RoleProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'CUSTOMER', 'GUEST']}>
 *     <Dashboard />
 *   </RoleProtectedRoute>
 * } />
 * ```
 */
export function RoleProtectedRoute({ allowedRoles }: { allowedRoles: UserRole[] }) {
  const { user, role, hasAnyRole, loading } = useAuth()

  console.log('🛡️ RoleProtectedRoute:', {
    hasUser: !!user,
    userRole: role,
    allowedRoles,
    hasAccess: hasAnyRole(allowedRoles),
    loading,
  })

  if (loading) {
    return <LoadingSpinner />
  }

  if (!user) {
    console.log('❌ Not authenticated, redirecting to login')
    return <Navigate to="/login" replace />
  }

  if (!hasAnyRole(allowedRoles)) {
    console.log('❌ Role not allowed, showing unauthorized message')
    return <UnauthorizedAccess requiredRole={allowedRoles.join(' or ')} />
  }

  console.log('✅ Role-based access granted!')
  return <Outlet />
}

/**
 * Any authenticated user can access (all roles allowed)
 *
 * This is a convenience component for routes that any logged-in user can access,
 * regardless of their role.
 *
 * @example
 * ```tsx
 * <Route path="/settings" element={
 *   <AuthenticatedRoute>
 *     <UserSettings />
 *   </AuthenticatedRoute>
 * } />
 * ```
 */
export function AuthenticatedRoute() {
  return <RoleProtectedRoute allowedRoles={['ADMIN', 'PARTNER', 'CUSTOMER', 'GUEST']} />
}
